import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import Modal from '../../components/ui/Modal';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import { api, getErrorMessage } from '../../lib/apiClient';
import WorkspaceLogoField from './WorkspaceLogoField';
import {
  deleteWorkspace,
  deleteWorkspaceImage,
  tenantKeys,
  updateWorkspace,
  uploadWorkspaceImage,
} from './tenancyApi';

/** Owner-only workspace settings: branding, rename, delete. */
export default function WorkspaceSettingsModal({ open, onClose, orgId, workspace, onDeleted }) {
  const queryClient = useQueryClient();
  const [name, setName] = useState(workspace?.name ?? '');
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

  useEffect(() => {
    if (open) setName(workspace?.name ?? '');
  }, [open, workspace?.name]);

  const invalidateWorkspaces = () =>
    queryClient.invalidateQueries({ queryKey: tenantKeys.workspaces(orgId) });

  const exportAudit = useMutation({
    mutationFn: async () => {
      const { data } = await api.get(`/workspaces/${workspace.id}/activity/export`, {
        responseType: 'blob',
      });
      const url = URL.createObjectURL(data);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'workspace-audit.pdf';
      link.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    },
    onError: async (error) => {
      let message = 'Audit export is available on Business and higher plans.';
      if (error.response?.data instanceof Blob) {
        try {
          message = JSON.parse(await error.response.data.text()).message ?? message;
        } catch {
          /* Keep the readable fallback. */
        }
      } else message = getErrorMessage(error, message);
      toast.error(message);
    },
  });

  const renameMutation = useMutation({
    mutationFn: () => updateWorkspace({ orgId, workspaceId: workspace.id, name: name.trim() }),
    onSuccess: () => {
      invalidateWorkspaces();
      toast.success('Workspace updated');
    },
    onError: (err) => toast.error(getErrorMessage(err, 'Could not update workspace')),
  });

  const uploadMutation = useMutation({
    mutationFn: (file) => uploadWorkspaceImage({ orgId, workspaceId: workspace.id, file }),
    onSuccess: (saved, file) => {
      // The bytes we just uploaded ARE the bytes to display, and the response
      // carries the new key — so seed the cache instead of making the avatar
      // round-trip back to the server for an image the browser already holds.
      queryClient.setQueryData(tenantKeys.workspaceImage(workspace.id, saved.imageKey), file);
      invalidateWorkspaces();
      toast.success('Workspace image updated');
    },
    onError: (err) => toast.error(getErrorMessage(err, 'Could not upload image')),
  });

  const removeImageMutation = useMutation({
    mutationFn: () => deleteWorkspaceImage({ orgId, workspaceId: workspace.id }),
    onSuccess: () => {
      // Drop every cached blob for this workspace so no stale key can resurface.
      queryClient.removeQueries({ queryKey: ['workspace-image', workspace.id] });
      invalidateWorkspaces();
      toast.success('Workspace image removed');
    },
    onError: (err) => toast.error(getErrorMessage(err, 'Could not remove image')),
  });

  const deleteMutation = useMutation({
    mutationFn: () => deleteWorkspace({ orgId, workspaceId: workspace.id }),
    onSuccess: () => {
      setConfirmDeleteOpen(false);
      invalidateWorkspaces();
      toast.success('Workspace deleted');
      onClose();
      onDeleted?.();
    },
    onError: (err) => toast.error(getErrorMessage(err, 'Could not delete workspace')),
  });

  if (!workspace) return null;
  const isPersonal = workspace.type === 'personal';

  return (
    <Modal open={open} onClose={onClose} title="Workspace settings" size="lg">
      <div className="space-y-5">
        {/* Branding hero — the logo and the identity it produces, side by side. */}
        <WorkspaceLogoField
          orgId={orgId}
          workspace={workspace}
          displayName={name}
          onFile={(file) => uploadMutation.mutateAsync(file)}
          onRemove={() => removeImageMutation.mutate()}
          uploading={uploadMutation.isPending}
          removing={removeImageMutation.isPending}
        />

        {/* Name */}
        <form
          className="flex items-end gap-2 border-t border-slate-200 pt-5"
          onSubmit={(e) => {
            e.preventDefault();
            if (name.trim() && name.trim() !== workspace.name) renameMutation.mutate();
          }}
        >
          <div className="flex-1">
            <Input label="Workspace name" value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <Button
            type="submit"
            loading={renameMutation.isPending}
            disabled={!name.trim() || name.trim() === workspace.name}
          >
            Save
          </Button>
        </form>

        <div className="border-t border-slate-200 pt-5">
          <p className="text-sm font-medium text-slate-700">Workspace audit log</p>
          <p className="mb-3 mt-1 text-xs text-slate-500">
            Download a PDF of workspace activity. Available on Business and higher plans.
          </p>
          <Button
            type="button"
            variant="secondary"
            loading={exportAudit.isPending}
            onClick={() => exportAudit.mutate()}
          >
            Export audit log as PDF
          </Button>
        </div>

        {/* Danger zone */}
        {!isPersonal && (
          <div className="border-t border-slate-200 pt-5">
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <p className="text-sm font-medium text-slate-700">Delete this workspace</p>
                <p className="text-xs text-slate-500">
                  Its files stay recoverable by support, but members lose access immediately.
                </p>
              </div>
              <Button
                type="button"
                variant="danger"
                size="sm"
                className="shrink-0"
                onClick={() => setConfirmDeleteOpen(true)}
              >
                Delete
              </Button>
            </div>
          </div>
        )}
      </div>

      <ConfirmDialog
        open={confirmDeleteOpen}
        onClose={() => setConfirmDeleteOpen(false)}
        onConfirm={() => deleteMutation.mutate()}
        loading={deleteMutation.isPending}
        title={`Delete "${workspace.name}"?`}
        description="Members will immediately lose access to everything in this workspace."
        confirmLabel="Delete workspace"
      />
    </Modal>
  );
}
